import { z } from "zod";
import type { CreateApprovedLocationRequest } from "@models/organization";
import {
    certificateFileError,
    CERTIFICATE_REQUIRED_MESSAGE,
} from "@utils/certificateFile";
import { addressFormFields, requiredText } from "./addressForm";
import { EMPTY_CERTIFICATE_MESSAGE } from "./locationForm";

// The New Location form (C4, API #427): the location's name, its address as
// Edit Location's, and the resale certificate the admin approves it with.

// Mirrors DomainConstraint.Location.Name on the API.
const NAME_MAX = 256;

// The marketplace is US-only (the state is one of the 50 + DC) and the form
// has no country field, as Edit Location sends US for a blank country.
const COUNTRY = "US";

export const addLocationFormSchema = z.object({
    name: requiredText("Location name", NAME_MAX),
    ...addressFormFields,
    // Checked on the field, so its message shows beside the others'. The API
    // refuses a missing or empty file; the shared rules check type and size.
    resaleCertificate: z.custom<FileList>().superRefine((files, context) => {
        const file = files?.[0];
        const message = !file
            ? CERTIFICATE_REQUIRED_MESSAGE
            : file.size === 0
                ? EMPTY_CERTIFICATE_MESSAGE
                : certificateFileError(file);

        if (message) {
            context.addIssue({ code: "custom", message });
        }
    }),
});

export type AddLocationFormValues = z.infer<typeof addLocationFormSchema>;

// The form opens empty; no file is chosen.
export const EMPTY_ADD_LOCATION_FORM = {
    name: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
};

// `values` are the schema's output: trimmed, with a certificate chosen.
export const toCreateApprovedLocationRequest = (
    values: AddLocationFormValues,
): CreateApprovedLocationRequest => ({
    name: values.name,
    streetAddress: values.streetAddress,
    city: values.city,
    state: values.state,
    zipCode: values.zipCode,
    country: COUNTRY,
    resaleCertificate: values.resaleCertificate[0],
});
